import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { ModeView } from '../../../shared/ontology/model/mode-view.enum';
import { canEditFieldByMetadata, isEditableByMode } from '../utils/ontology-editing.util';

type Item = any;

@Component({
    selector: 'jhi-multi-select',
    template: `
		@if (this.modeView !== ModeView.VIEW) {
			<div class="jhi-ng-select-shell">
				<ng-select
					[items]="displayOptions"
					[placeholder]="placeholder"
					[multiple]="true"
					[searchable]="true"
					[clearable]="false"
					[hideSelected]="true"
					[bindLabel]="labelField"
					[trackByFn]="trackByFn"
					[compareWith]="compareWithFn"
					[searchFn]="searchFn"
					[ngModelOptions]="{ standalone: true }"
					[ngModel]="selected"
					(change)="onChangeInternal($event)"
					(remove)="onRemove($event)"
					(keydown)="onKeyDown($event)"
					(blur)="markTouched()">
					<ng-template ng-label-tmp let-item="item" let-clear="clear">
						<span class="label label-primary jhi-chip">
							{{ labelFn(item) }}
							<a *ngIf="canRemove(item)"
							   href="#"
							   class="jhi-chip-remove"
							   (click)="$event.preventDefault(); clear(item)">
								<span class="fas fa-times"></span>
							</a>
						</span>
					</ng-template>
					<ng-template ng-option-tmp let-item="item">
						<div [class.ng-option-disabled]="item.disabled">
							{{ labelFn(item) }}
						</div>
					</ng-template>
				</ng-select>
			</div>
		} @else {
			<div class="form-control-plaintext">
				<truncate-span [textInput]="formatViewValue(selected)"></truncate-span>
			</div>
		}
    `,
    styles: [
        `
            :host.ng-invalid.ng-touched .jhi-ng-select-shell ::ng-deep .ng-select-container {
                border-left: 6px solid var(--bs-danger) !important;
                padding-left: calc(.75rem - 6px) !important;
            }

            :host.ng-valid.ng-touched .jhi-ng-select-shell ::ng-deep .ng-select-container {
                border-left: 6px solid var(--bs-success) !important;
                padding-left: calc(.75rem - 6px) !important;
            }

            .jhi-chip {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                margin-right: 4px;
                padding: 4px 8px;
            }

            .jhi-chip-remove {
                cursor: pointer;
                opacity: 0.9;
            }

            .jhi-chip-remove:hover {
                opacity: 1;
            }
        `
    ],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MultiSelectComponent), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => MultiSelectComponent), multi: true }
    ]
})
export class MultiSelectComponent implements ControlValueAccessor, Validator {
    protected readonly ModeView = ModeView;

    @Input() modeView: ModeView = ModeView.VIEW;
    @Input() model: any;
    @Input() property?: string;

    @Input() disabled = false;
    @Input() options: Item[] = [];

    @Input() placeholder = '';
    @Input() appendable = true;

    /** required opcional */
    @Input()
    set required(v: boolean) {
        this._required = !!v;
        this._validatorChange?.();
    }

    get required(): boolean {
        return this._required;
    }

    private _required = false;

    @Input() idField = 'id';
    @Input() labelField = 'name';
    @Input() labelFn: (opt: Item) => string = (opt: Item) => String(opt?.[this.labelField] ?? '');

    @Output() valueChange = new EventEmitter<Item[]>();

    selected: Item[] = [];
    private lockedIds = new Set<string>();

    private onChange: (v: Item[]) => void = () => {
    };
    private onTouched: () => void = () => {
    };
    private _validatorChange?: () => void;

    writeValue(v: any[] | null): void {
        const incoming = Array.isArray(v) ? v : [];
        this.selected = incoming;

        if (!this.selected || this.selected.length > 0) {
            if (!canEditFieldByMetadata(this.model, this.property) && this.appendable) {
                this.lockedIds = new Set(incoming.map(i => String(i?.[this.idField])));
            } else {
                this.lockedIds.clear();
            }
        }
    }

    get displayOptions(): any[] {
        const locked = this.lockedIds;
        return (this.options ?? []).map(o => ({
            ...o,
            disabled: !this.isEditable() && locked.has(String(o?.[this.idField]))
        }));
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    markTouched(): void {
        this.onTouched();
    }

    onChangeInternal(v: Item[] | null): void {
        this.selected = Array.isArray(v) ? v : [];
        this.onChange(this.selected);
        this.valueChange.emit(this.selected);
        this._validatorChange?.();
    }

    protected applyDisabledToOptions(): void {
        if (!this.options) {
            return;
        }

        const locked = this.lockedIds;
        this.options = this.options.map(o => ({
            ...o,
            disabled: locked.has(String(o?.[this.idField]))
        }));

    }

    canRemove(item: any): boolean {
        const id = String(item?.[this.idField]);
        return !this.lockedIds.has(id);
    }

    onRemove(item: Item): void {
        if (this.canRemove(item)) {
            return;
        }
        this.selected = this.dedupById([...(this.selected ?? []), item]);
        this.onChange(this.selected);
        this.valueChange.emit(this.selected);
        this._validatorChange?.();
    }

    searchFn = (term: string, item: any): boolean => {
        const t = (term ?? '').toLowerCase().trim();
        if (!t) {
            return true;
        }
        return (this.labelFn(item) ?? '').toLowerCase().includes(t);
    };

    onKeyDown(evt: KeyboardEvent): void {
        if (evt.key !== 'Backspace') {
            return;
        }

        const last = (this.selected ?? [])[this.selected.length - 1];
        if (last && !this.canRemove(last)) {
            evt.preventDefault();
            evt.stopPropagation();
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (!this.required) {
            return null;
        }

        const v = control.value;
        if (!Array.isArray(v) || v.length === 0) {
            return { emptyValue: true };
        }
        return null;
    }

    registerOnValidatorChange(fn: () => void): void {
        this._validatorChange = fn;
    }

    compareWithFn = (a: any, b: any): boolean => {
        const aId = a?.[this.idField];
        const bId = b?.[this.idField];
        return aId != null && bId != null && String(aId) === String(bId);
    };

    trackByFn = (_: number, item: any): any => item?.[this.idField] ?? item;

    formatViewValue(v: Item[] | null): string {
        if (!v || v.length === 0) {
            return '-';
        }
        return v.map(x => this.labelFn(x)).join(', ');
    }

    isEditable(): boolean {
        return isEditableByMode({
            modeView: this.modeView,
            model: this.model,
            property: this.property ?? ''
        });
    }

    private dedupById(items: any[]): any[] {
        const seen = new Set<string>();
        const out: any[] = [];
        for (const it of items) {
            const id = String(it?.[this.idField]);
            if (!seen.has(id)) {
                seen.add(id);
                out.push(it);
            }
        }
        return out;
    }
}
